import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { validateInvitationToken } from "../../services/invitation-service";
import AuthService from "../../services/auth-services";
import { BASE_URL } from "../../services/api";
import LoadingOverlay from "../../components/UI/LoadingOverlay/LoadingOverlay";
import styles from "./RegisterPage.module.css";
import SoftindexLogo from "../../assets/images/softindex-website-logo.webp";

export default function RegisterPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tokenValid, setTokenValid] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
    isValid: false,
  });
  const [passwordMatch, setPasswordMatch] = useState({
    isMatching: false,
    hasValue: false,
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { checkAuthStatus } = useAuth();

  const validatePasswordStrength = (password) => {
    const feedback = [];
    let score = 0;

    if (password.length < 8) {
      feedback.push("At least 8 characters");
    } else {
      score += 1;
    }

    if (!/[A-Z]/.test(password)) {
      feedback.push("One uppercase letter (A-Z)");
    } else {
      score += 1;
    }

    if (!/[a-z]/.test(password)) {
      feedback.push("One lowercase letter (a-z)");
    } else {
      score += 1;
    }

    if (!/\d/.test(password)) {
      feedback.push("One number (0-9)");
    } else {
      score += 1;
    }

    if (!/[^A-Za-z0-9]/.test(password)) {
      feedback.push("One special character (!@#$%^&*)");
    } else {
      score += 1;
    }

    return {
      score,
      feedback,
      isValid: score >= 4 && password.length >= 8,
    };
  };

  const checkPasswordMatch = (password, confirmPassword) => {
    if (!confirmPassword) {
      return { isMatching: false, hasValue: false };
    }
    return {
      isMatching: password === confirmPassword,
      hasValue: true,
    };
  };

  const validateToken = useCallback(async () => {
    setLoading(true);
    try {
      const response = await validateInvitationToken(token);

      if (response.success) {
        setTokenValid(true);
        setInvitationData(response.data);
      } else {
        setTokenValid(false);
        setError(response.error || "Invalid or expired invitation token.");
      }
    } catch {
      setTokenValid(false);
      setError("Failed to validate invitation. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const registrationStatus = searchParams.get("registration");
    const errorParam = searchParams.get("error");

    if (registrationStatus === "success") {
      navigate("/");
      return;
    }

    if (errorParam) {
      const errorMessages = {
        missing_invitation_data:
          "Missing invitation data. Please use the link from your invitation email.",
        invalid_invitation: "Invalid or expired invitation token.",
        email_mismatch:
          "Your Google account email doesn't match the invited email address.",
        user_already_exists: "An account with this email already exists.",
        registration_failed: "Registration failed. Please try again.",
      };
      setError(
        errorMessages[errorParam] || "An error occurred during registration."
      );
    }

    if (!token) {
      setError(
        "Invalid registration link. Please check your invitation email."
      );
      setTokenValid(false);
      return;
    }

    validateToken();
  }, [token, searchParams, navigate, validateToken]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "password") {
        const strength = validatePasswordStrength(value);
        setPasswordStrength(strength);
        if (prev.confirmPassword) {
          setPasswordMatch(checkPasswordMatch(value, prev.confirmPassword));
        } else {
          setPasswordMatch({ isMatching: false, hasValue: false });
        }
      }
      if (name === "confirmPassword") {
        setPasswordMatch(checkPasswordMatch(prev.password, value));
      }
      return { ...prev, [name]: value };
    });
  };

  const handleRegister = async () => {
    if (!formData.fullName.trim() || !formData.password.trim()) {
      setError("Please fill in all required fields.");
      return;
    }

    if (!passwordStrength.isValid) {
      setError("Password does not meet strength requirements.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const registrationData = {
        token,
        email: invitationData?.invitation?.email,
        name: formData.fullName.trim(),
        password: formData.password,
      };

      await AuthService.registerWithToken(registrationData);
      await checkAuthStatus(true);
      navigate("/?registration=success");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to complete registration. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    if (!token || !invitationData?.invitation?.email) {
      setError("Invalid invitation data. Please try again.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${BASE_URL}/auth/store-invitation-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            token,
            email: invitationData.invitation.email,
          }),
        }
      );

      if (!response.ok) {
        await response.text();
        throw new Error("Failed to store invitation data");
      }

      await response.json();
      window.location.href = `${BASE_URL}/auth/google/register`;
    } catch {
      setError("Failed to initiate Google registration. Please try again.");
      setLoading(false);
    }
  };

  if (loading && tokenValid === null) {
    return (
      <LoadingOverlay
        isVisible={true}
        message="Validating invitation..."
        maxDisplayTime={500}
      />
    );
  }

  if (tokenValid === false) {
    return (
      <div className={styles.registerPage}>
        <div className={styles.container}>
          <div className={styles.logoSection}>
            <img
              src={SoftindexLogo || "/placeholder.svg"}
              alt="Softindex Logo"
              className={styles.logo}
            />
          </div>
          <div className={styles.content}>
            <h1 className={styles.title}>Invalid Invitation</h1>
            <div className={styles.errorMessage}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={styles.errorIcon}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {error}
            </div>
            <button
              type="button"
              onClick={() => navigate("/auth")}
              className={styles.actionButton}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.registerPage}>
      <div className={styles.container}>
        <div className={styles.logoSection}>
          <img
            src={SoftindexLogo || "/placeholder.svg"}
            alt="Softindex Logo"
            className={styles.logo}
          />
        </div>

        <div className={styles.content}>
          <h1 className={styles.title}>Complete Your Registration</h1>
          <p className={styles.subtitle}>
            You&apos;ve been invited to join Softindex. Please complete your
            account setup below.
          </p>

          {invitationData && (
            <div className={styles.invitationInfo}>
              <p>
                Email: <strong>{invitationData.invitation?.email}</strong>
              </p>
            </div>
          )}

          {error && (
            <div className={styles.errorMessage}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={styles.errorIcon}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {error}
            </div>
          )}

          <div className={styles.registerForm}>
            <div className={styles.formContainer}>
              <div className={styles.inputGroup}>
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={styles.input}
                />

                <div className={styles.passwordInputContainer}>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Create Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={styles.passwordInput}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={styles.passwordToggle}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>

                {formData.password && (
                  <div className={styles.passwordFeedback}>
                    <div className={styles.strengthIndicator}>
                      <div className={styles.strengthBar}>
                        <div
                          className={`${styles.strengthFill} ${
                            passwordStrength.score >= 1 ? styles.weak : ""
                          } ${
                            passwordStrength.score >= 3 ? styles.medium : ""
                          } ${
                            passwordStrength.score >= 4 ? styles.strong : ""
                          }`}
                          style={{
                            width: `${(passwordStrength.score / 5) * 100}%`,
                          }}
                        ></div>
                      </div>
                      <span
                        className={`${styles.strengthText} ${
                          passwordStrength.score >= 4
                            ? styles.strong
                            : passwordStrength.score >= 3
                              ? styles.medium
                              : styles.weak
                        }`}
                      >
                        {passwordStrength.score >= 4
                          ? "Strong"
                          : passwordStrength.score >= 3
                            ? "Medium"
                            : passwordStrength.score >= 1
                              ? "Weak"
                              : "Very Weak"}
                      </span>
                    </div>
                    {passwordStrength.feedback.length > 0 && (
                      <div className={styles.requirementsList}>
                        {passwordStrength.feedback.map((requirement, index) => (
                          <div key={index} className={styles.requirement}>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              className={styles.requirementIcon}
                            >
                              <circle cx="12" cy="12" r="10" />
                              <line x1="15" y1="9" x2="9" y2="15" />
                              <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                            <span>{requirement}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className={styles.passwordInputContainer}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={styles.passwordInput}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={styles.passwordToggle}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>

                {passwordMatch.hasValue && (
                  <div className={styles.passwordMatchFeedback}>
                    <div
                      className={`${styles.matchIndicator} ${
                        passwordMatch.isMatching ? styles.match : styles.noMatch
                      }`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className={styles.matchIcon}
                      >
                        {passwordMatch.isMatching ? (
                          <>
                            <path d="M20 6L9 17l-5-5" />
                          </>
                        ) : (
                          <>
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                          </>
                        )}
                      </svg>
                      <span>
                        {passwordMatch.isMatching
                          ? "Passwords match"
                          : "Passwords do not match"}
                      </span>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleRegister}
                  disabled={
                    loading ||
                    !passwordStrength.isValid ||
                    !passwordMatch.isMatching
                  }
                  className={styles.actionButton}
                >
                  {loading ? (
                    <div className={styles.spinner}></div>
                  ) : (
                    "Complete Registration"
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className={styles.divider}>
            <span>or</span>
          </div>

          <button
            type="button"
            onClick={handleGoogleRegister}
            disabled={loading}
            className={styles.googleButton}
          >
            <div className={styles.buttonContent}>
              {loading ? (
                <div className={styles.spinner}></div>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  className={styles.googleIcon}
                >
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              <span className={styles.buttonText}>
                {loading ? "Registering..." : "Continue with Google"}
              </span>
            </div>
          </button>

          <div className={styles.footer}>
            <p className={styles.footerText}>
              By registering, you agree to our{" "}
              <a href="/terms" className={styles.footerLink}>
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" className={styles.footerLink}>
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
